import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useStore } from "react-redux";
import { t } from "@bookingcom/lingojs-core";
import useGlobalContext from "../hooks/useGlobalContext.js";
import { assert } from "../utils/assert.js";
import { PARAM_LIST_SEPARATOR } from "@flights/constants";
import useCreateUrl from "../hooks/useCreateUrl.js";
import { PRE_OPTED_ANCILLARIES_QUERY_PARAMS, BRANDED_FARES_QUERY_PARAMS, PRE_FETCHED_FARES_QUERY_PARAMS } from "../constants.js";
import { getFlightToken } from "../store/flightDetails/selectors.js";
import { getCartToken } from "../store/cartDetails/selectors.js";
import { getOrderToken } from "../store/order/selectors.js";
import { getSearch } from "../store/search/selectors.js";
import flights_apex_web_flextras_as_ancillaries_v4_ddot from "../utils/experiments/apex/flights_apex_web_flextras_as_ancillaries_v4_ddot.js";
import { track } from "@flights/et-universal";
const isMobile = typeof window !== "undefined" && Boolean(window.__GLOBAL_CONTEXT__?.userAgent?.isMobile);
const isPaxWithBaggages = !isMobile;
const screens = {
    home: {
        title: t("flights_index_header_compare_book_ease"),
        url: () => "/"
    },
    "checkout-start": {
        title: t("flights_page_title_bp_generic"),
        url: () => {
            throw Error("Unable to build `checkout-start` url. Flight id is not yet known in store.");
        }
    },
    "checkout-ticket-type": {
        title: t("flights_progress_ticket_type"),
        getTitleOverride: () => !isMobile && !!flights_apex_web_flextras_as_ancillaries_v4_ddot.trackWithDefaultStage()
            ? t("flights_checkout_stepper_flexibility")
            : t("flights_progress_ticket_type"),
        url: (context) => {
            const { flightToken, cartToken } = context;
            assert(flightToken, "Missing flight details for checkout nav");
            assert(cartToken, "Missing cart token");
            return `/checkout/ticket-type/${flightToken}/${cartToken}?${getQueryParamsToPreserve().toString()}`;
        }
    },
    "checkout-fare": {
        title: t("flights_checkout_stepper_choose_fare"),
        url: (context) => {
            const { flightToken, cartToken } = context;
            assert(flightToken, "Missing flight details for checkout nav");
            assert(cartToken, "Missing cart token");
            return `/checkout/fare/${flightToken}/${cartToken}?${getQueryParamsToPreserve().toString()}`;
        }
    },
    "checkout-pax": {
        title: isPaxWithBaggages
            ? t("flights_checkout_stepper_your_details")
            : t("flights_checkout_passenger_details_stepper"),
        url: (context) => {
            const { flightToken, cartToken } = context;
            assert(flightToken, "Missing flight details for checkout nav");
            assert(cartToken, "Missing cart token");
            return `/checkout/pax/${flightToken}/${cartToken}?${getQueryParamsToPreserve().toString()}`;
        }
    },
    "checkout-flexibility": {
        title: t("flights_ancillaries_flexibility_heading"),
        getTitleOverride: () => isMobile && track("flights_apex_modular_checkout_mdot") === 1
            ? t("flights_checkout_stepper_bags_flex_mdot")
            : t("flights_ancillaries_flexibility_heading"),
        url: (context) => {
            const { flightToken, cartToken } = context;
            assert(flightToken, "Missing flight details for checkout nav");
            assert(cartToken, "Missing cart token");
            return `/checkout/flexibility/${flightToken}/${cartToken}?${getQueryParamsToPreserve().toString()}`;
        }
    },
    "checkout-extras": {
        title: isPaxWithBaggages || (isMobile && track("flights_apex_modular_checkout_mdot") === 1)
            ? t("flights_checkout_stepper_extras")
            : t("flights_stepper_baggage"),
        url: (context) => {
            const { flightToken, cartToken } = context;
            assert(flightToken, "Missing flight details for checkout nav");
            assert(cartToken, "Missing cart token");
            return `/checkout/extras/${flightToken}/${cartToken}?${getQueryParamsToPreserve().toString()}`;
        }
    },
    "checkout-seat-selection": {
        title: t("flights_seatmap_dt_header_select", { num_exception: 1 }),
        url: (context) => {
            const { flightToken, cartToken } = context;
            assert(flightToken, "Missing flight details for checkout nav");
            assert(cartToken, "Missing cart token");
            return `/checkout/seats/${flightToken}/${cartToken}`;
        }
    },
    "checkout-sirf": {
        title: t("flights_checkout_review_stepper"),
        url: (context) => {
            const { flightToken, cartToken } = context;
            assert(flightToken, "Missing flight details for checkout nav");
            assert(cartToken, "Missing cart token");
            return `/checkout/sirf/${flightToken}/${cartToken}`;
        }
    },
    "checkout-payment": {
        title: t("flights_checkout_review_stepper"),
        url: (context) => {
            const { flightToken, orderToken } = context;
            assert(flightToken, "Missing flight details for checkout nav");
            assert(orderToken, "Missing orderToken");
            return `/checkout/payment/${flightToken}/${orderToken}`;
        }
    },
    "checkout-collection": {
        title: t("flights_checkout_review_stepper"),
        url: (context) => {
            const { flightToken, orderToken } = context;
            assert(flightToken, "Missing flight details for checkout nav");
            assert(orderToken, "Missing orderToken");
            return `/checkout/payment/${flightToken}/${orderToken}`;
        }
    }
};
const getQueryParamsToPreserve = () => {
    const paramsToPreserve = pickParamsFromCurrentUrl(Object.values({
        ...PRE_OPTED_ANCILLARIES_QUERY_PARAMS,
        ...BRANDED_FARES_QUERY_PARAMS,
        ...PRE_FETCHED_FARES_QUERY_PARAMS
    }));
    return new URLSearchParams(paramsToPreserve);
};
const pickParamsFromCurrentUrl = (params) => {
    const currentParams = new URLSearchParams(location.search);
    const picked = {};
    params.forEach((param) => {
        if (currentParams.has(param)) {
            picked[param] = currentParams.get(param);
        }
    });
    return picked;
};
const DEEPLINKS_PROTOCOL = "booking:";
function searchParamsFromState(search) {
    const params = new URLSearchParams();
    const { searchSegments } = search;
    const { from, to, departureDate, returnDate } = searchSegments[0];
    params.set("type", search.type);
    params.set("adults", `${search.adults}`);
    params.set("cabinClass", search.cabinClass);
    params.set("children", search.children.join(PARAM_LIST_SEPARATOR));
    params.set("depart", departureDate || "");
    params.set("sort", search.sort);
    params.set("from", from.map((l) => l.code).join(PARAM_LIST_SEPARATOR));
    params.set("to", to.map((l) => l.code).join(PARAM_LIST_SEPARATOR));
    if (search.type == "ROUNDTRIP") {
        params.set("return", `${returnDate}`);
    }
    return params;
}
const nativeAppScreens = {
    home: {
        title: t("flights_index_header_compare_book_ease"),
        url: function (context) {
            const params = searchParamsFromState(context.search);
            return `${DEEPLINKS_PROTOCOL}//flights?${params}`;
        }
    }
};
export const getScreen = (routeName) => {
    const screen = screens[routeName];
    if (!screen) {
        throw Error(`Invalid screen: ${routeName}`);
    }
    return screen;
};
export const getScreenTitle = (routeName) => {
    return screens[routeName]?.getTitleOverride?.() || screens[routeName]?.title;
};
export const getNativeScreen = (routeName) => {
    const screen = nativeAppScreens[routeName];
    if (!screen) {
        throw Error(`Invalid native screen: ${routeName}`);
    }
    return screen;
};
export const useScreenUrl = (routeName, options) => {
    const { createUrl } = useCreateUrl();
    const isNativeScreen = useIsNativeScreen();
    const store = useStore();
    let screen = undefined;
    if (!options?.doNotThrowErrorWithoutTriggeringAction) {
        screen = isNativeScreen ? getNativeScreen(routeName) : getScreen(routeName);
    }
    const getScreenUrl = useCallback(() => {
        const routeContext = getRouteContext(store.getState());
        const localScreen = screen || (isNativeScreen ? getNativeScreen(routeName) : getScreen(routeName));
        const url = localScreen.url(routeContext);
        return createUrl(url);
    }, [createUrl, isNativeScreen, routeName, screen, store]);
    return { getScreenUrl };
};
export const useScreenNavigation = (routeName) => {
    const isNativeScreen = useIsNativeScreen();
    const { getScreenUrl } = useScreenUrl(routeName);
    const history = useHistory();
    return useCallback(() => {
        if (isNativeScreen) {
            window.location.href = getScreenUrl();
        }
        else {
            history.push(getScreenUrl());
        }
    }, [history, getScreenUrl, isNativeScreen]);
};
const getRouteContext = (state) => {
    const flightToken = getFlightToken(state);
    const cartToken = getCartToken(state);
    const orderToken = getOrderToken(state);
    const search = getSearch(state);
    return { search, flightToken, cartToken, orderToken };
};
const useIsNativeScreen = () => {
    const { userAgent, features } = useGlobalContext();
    return !!userAgent.isWebview && !!features.FLIGHTS_ENABLE_APP_DEEPLINKS;
};
